import {computed, inject, Injectable, Signal} from '@angular/core';
import {SearchService} from '../data/search.service';
import {Facets, FacetKey, FacetNode} from '../domain/search.model';
import {toSignal} from '@angular/core/rxjs-interop';
import {map, scan, Subject, switchMap} from 'rxjs';
import {Scheme} from '../../scheme/domain/scheme.model';

interface SearchTrigger {
  cursor: string | null;
  filters: Facets | null;
  query: string | null;
  reset: boolean;
}

interface SearchState {
  schemes: Scheme[];
  hasMore: boolean;
  nextCursor: string | null;
  facets: Facets;
}

const emptyFacets: Facets = {
  [FacetKey.PRODUCTS]: [],
  [FacetKey.PRODUCTS_CASCADE]: [],
  [FacetKey.CONTROL_PLATFORMS]: [],
};

const initialState: SearchState = {
  schemes: [],
  hasMore: false,
  nextCursor: null,
  facets: emptyFacets,
};

@Injectable({ providedIn: 'root' })
export class SearchFacade {
  private readonly _searchService = inject(SearchService);

  private readonly _country = 'DE';
  private readonly _language = 'de';

  // Remembered filters so cursor pages know what filters are active
  private _currentFilters: Facets | null = null;
  private _currentQuery: string | null = null;

  private readonly _trigger$ = new Subject<SearchTrigger>();

  private readonly _state = toSignal(
    this._trigger$.pipe(
      switchMap(trigger =>
        this._searchService.search({
          country: this._country,
          language: this._language,
          filters: trigger.filters,
          query: trigger.query,
          cursor: trigger.cursor,
        }).pipe(
          map(result => ({ result, reset: trigger.reset }))
        )
      ),
      scan((acc: SearchState, { result, reset }) => ({
        schemes: reset ? result.items : [...acc.schemes, ...result.items],
        hasMore: result.page.hasMore,
        nextCursor: result.page.nextCursor,
        // Cursor responses don't return facets – keep the previous ones
        facets: result.facets ?? acc.facets,
      }), initialState)
    ),
    { initialValue: initialState }
  );

  public readonly schemes: Signal<Scheme[]> = computed(() => this._state().schemes);
  public readonly facetNodes: Signal<Facets> = computed(() => this._state().facets);

  constructor() {
    this._trigger$.next({ cursor: null, filters: null, query: null, reset: true });
  }

  public search(key: FacetKey, facets: FacetNode[]): void {
    this._currentFilters = {
      ...(this._currentFilters ?? emptyFacets),
      [key]: facets,
    };
    this._trigger$.next({ cursor: null, filters: this._currentFilters, query: this._currentQuery, reset: true });
  }

  public searchByQuery(query: string | null): void {
    this._currentQuery = query;
    this._trigger$.next({ cursor: null, filters: this._currentFilters, query: this._currentQuery, reset: true });
  }

  public searchNext(): void {
    const { hasMore, nextCursor } = this._state();
    if (!hasMore || !nextCursor) return;
    this._trigger$.next({ cursor: nextCursor, filters: null, query: null, reset: false });
  }
}
